import { apiClient } from "./base.service";
import type {
  BusinessPlanContext,
  BusinessPlanDocument,
  CreateInvestorLinkResult,
  InvestorComment,
  InvestorLinkSummary,
} from "@/types/startup.types";

const SESSION_STORAGE_PREFIX = "burnchart_investor_";

export interface InvestorSession {
  sessionId: string;
  expiresAt: string;
  startupId: string;
  linkId: string;
  linkLabel: string;
  token: string;
  displayName: string;
  email?: string | null;
}

export function getInvestorSession(token: string): InvestorSession | null {
  const raw = localStorage.getItem(`${SESSION_STORAGE_PREFIX}${token.slice(0, 12)}`);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as InvestorSession;
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(`${SESSION_STORAGE_PREFIX}${token.slice(0, 12)}`);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function saveInvestorSession(token: string, session: InvestorSession): void {
  localStorage.setItem(
    `${SESSION_STORAGE_PREFIX}${token.slice(0, 12)}`,
    JSON.stringify(session),
  );
}

export function clearInvestorSession(token: string): void {
  localStorage.removeItem(`${SESSION_STORAGE_PREFIX}${token.slice(0, 12)}`);
}

function investorHeaders(session: InvestorSession) {
  return {
    "Content-Type": "application/json",
    "x-investor-session": session.sessionId,
  };
}

function investorTokenPath(token: string): string {
  return encodeURIComponent(token);
}

export class InvestorAPIService {
  async getLinkInfo(token: string) {
    return apiClient.get<{
      label: string;
      startupName: string;
      tagline: string | null;
      description?: string;
      available: boolean;
      reason: string | null;
      investorContact: Record<string, string> | null;
      hasBusinessPlan?: boolean;
    }>(`/investor/${investorTokenPath(token)}/info`);
  }

  async verifyAccess(
    token: string,
    payload: { passcode: string; displayName: string; email?: string },
  ) {
    return apiClient.post<{
      sessionId: string;
      expiresAt: string;
      startupId: string;
      linkId: string;
      linkLabel: string;
    }>(`/investor/${investorTokenPath(token)}/verify`, payload);
  }

  async getData(session: InvestorSession) {
    return apiClient.get<Record<string, unknown>>(
      `/investor/${investorTokenPath(session.token)}/data`,
      {
        headers: investorHeaders(session),
      },
    );
  }

  async getComments(session: InvestorSession) {
    return apiClient.get<InvestorComment[]>(
      `/investor/${investorTokenPath(session.token)}/comments`,
      {
        headers: investorHeaders(session),
      },
    );
  }

  async postComment(session: InvestorSession, section: string, text: string) {
    return apiClient.post<InvestorComment>(
      `/investor/${investorTokenPath(session.token)}/comments`,
      { section, text },
      { headers: investorHeaders(session) },
    );
  }
}

export class InvestorFounderAPIService {
  private async getAuthHeaders() {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  async listLinks(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.get<InvestorLinkSummary[]>(
      `/startups/${startupId}/investor-links`,
      { headers },
    );
  }

  async createLink(
    startupId: string,
    data: { label: string; passcode: string; expiresAt?: string | null },
  ) {
    const headers = await this.getAuthHeaders();
    return apiClient.post<CreateInvestorLinkResult>(
      `/startups/${startupId}/investor-links`,
      data,
      { headers },
    );
  }

  async revokeLink(startupId: string, linkId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.delete<{ success: boolean }>(
      `/startups/${startupId}/investor-links/${linkId}`,
      { headers },
    );
  }

  async getShareUrl(startupId: string, linkId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.get<{ url: string }>(
      `/startups/${startupId}/investor-links/${linkId}/share-url`,
      { headers },
    );
  }

  async rotateLinkToken(startupId: string, linkId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.post<{ url: string; warning: string; label: string }>(
      `/startups/${startupId}/investor-links/${linkId}/rotate-token`,
      undefined,
      { headers },
    );
  }

  async updateLinkPin(startupId: string, linkId: string, passcode: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.patch<{ success: boolean }>(
      `/startups/${startupId}/investor-links/${linkId}`,
      { passcode },
      { headers },
    );
  }

  async getComments(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.get<InvestorComment[]>(
      `/startups/${startupId}/investor-comments`,
      { headers },
    );
  }

  async replyToComment(startupId: string, commentId: string, text: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.post<{ success: boolean }>(
      `/startups/${startupId}/investor-comments/${commentId}/replies`,
      { text },
      { headers },
    );
  }

  async markCommentsRead(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.post<{ success: boolean }>(
      `/startups/${startupId}/investor-comments/mark-read`,
      {},
      { headers },
    );
  }

  async getBusinessPlanContext(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.get<BusinessPlanContext>(
      `/startups/${startupId}/business-plan-context`,
      { headers },
    );
  }

  async updateBusinessPlanContext(startupId: string, data: BusinessPlanContext) {
    const headers = await this.getAuthHeaders();
    return apiClient.put<BusinessPlanContext>(
      `/startups/${startupId}/business-plan-context`,
      data,
      { headers },
    );
  }

  async patchBusinessPlanContext(
    startupId: string,
    data: Partial<BusinessPlanContext>,
  ) {
    const headers = await this.getAuthHeaders();
    return apiClient.patch<BusinessPlanContext>(
      `/startups/${startupId}/business-plan-context`,
      data,
      { headers },
    );
  }

  async generateBusinessPlan(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.post<BusinessPlanDocument>(
      `/startups/${startupId}/business-plan/generate`,
      {},
      { headers },
    );
  }

  async getBusinessPlan(startupId: string) {
    const headers = await this.getAuthHeaders();
    try {
      return await apiClient.get<BusinessPlanDocument>(
        `/startups/${startupId}/business-plan`,
        { headers },
      );
    } catch (error) {
      if (error instanceof Error && error.message.includes("404")) {
        return null;
      }
      throw error;
    }
  }
}

export const investorAPI = new InvestorAPIService();
export const investorFounderAPI = new InvestorFounderAPIService();
